import { useEffect, useRef, useState } from 'react';

import { useWhatsAppEmbeddedSignup } from '../../hooks/useWhatsAppEmbeddedSignup';

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <span className="text-body font-sf-pro-display text-text-secondary shrink-0">
        {label}
      </span>
      <span className="text-body font-sf-pro-display text-text-primary break-all text-left sm:text-right">
        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
      </span>
    </div>
  );
}

function Spinner({ className = 'h-5 w-5 text-surface-mint' }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function WhatsAppSetupForm({
  companyId,
  onSuccess,
  successHint = 'Continuing to activation…',
}) {
  const [pin, setPin] = useState('');
  const pinInputRef = useRef(null);
  const successHandledRef = useRef(false);

  const activeCompanyId =
    companyId ||
    (() => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          return userObj?.company_id || localStorage.getItem('company_id');
        }
      } catch {
        // fall through
      }
      return localStorage.getItem('company_id');
    })();

  const {
    status,
    connection,
    errorMessage,
    pinRequired,
    isConfigLoading,
    isReady,
    isConnecting,
    isConnected,
    launchSignup,
    resetError,
    refetchConfig,
  } = useWhatsAppEmbeddedSignup({ companyId: activeCompanyId, pin });

  useEffect(() => {
    if (pinRequired && pinInputRef.current) {
      pinInputRef.current.focus();
    }
  }, [pinRequired]);

  useEffect(() => {
    if (!isConnected || !connection || successHandledRef.current) return;
    successHandledRef.current = true;
    const timer = window.setTimeout(() => {
      onSuccess?.(connection);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [isConnected, connection, onSuccess]);

  const handleConnect = () => {
    resetError();
    launchSignup();
  };

  const handleRetry = () => {
    resetError();
    if (!isReady) {
      refetchConfig();
    }
  };

  if (isConfigLoading && status === 'idle') {
    return (
      <div className="w-full flex flex-col items-center gap-3 py-8 text-text-secondary font-sf-pro-display">
        <Spinner className="h-6 w-6 text-text-secondary" />
        <p className="text-body-lg">Loading WhatsApp connection…</p>
      </div>
    );
  }

  if (isConnected && connection) {
    const headline =
      connection.registration_action === 'newly_registered'
        ? 'WhatsApp connected and number registered'
        : 'WhatsApp connected';

    return (
      <div className="w-full flex flex-col gap-4 text-left">
        <div className="rounded-input border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
          <p className="text-body-lg font-sf-pro-display text-emerald-400 font-medium">
            {headline}
          </p>
          <p className="text-body font-sf-pro-display text-text-secondary mt-1">
            {successHint}
          </p>
        </div>
        <div className="flex flex-col gap-3 rounded-input border border-border-default bg-input-background p-4">
          <DetailRow label="Phone" value={connection.display_phone_number} />
          <DetailRow label="WABA ID" value={connection.waba_id} />
          <DetailRow
            label="Phone number ID"
            value={connection.phone_number_id}
          />
          <DetailRow label="Registered" value={connection.is_registered} />
          <DetailRow
            label="Registration"
            value={connection.registration_action}
          />
          <DetailRow
            label="Webhook subscribed"
            value={connection.webhook_subscribed}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-5 text-left">
      <p className="text-[14px] text-gray-500 leading-relaxed">
        Connect your WhatsApp Business account with Meta Embedded Signup. A Meta
        popup will open — finish the steps there to link your number.
      </p>

      <div className="flex flex-col gap-3">
        <label
          htmlFor="whatsappPin"
          className="text-[15px] font-medium text-gray-900"
        >
          WhatsApp 2FA PIN{' '}
          <span className="text-gray-500 font-normal">(optional)</span>
        </label>

        <div
          className="flex gap-2 sm:gap-3"
          onPaste={(e) => {
            e.preventDefault();
            const pastedData = e.clipboardData
              .getData('text/plain')
              .replace(/\D/g, '')
              .slice(0, 6);
            if (pastedData) {
              setPin(pastedData);
              if (errorMessage) resetError();
            }
          }}
        >
          {[0, 1, 2, 3, 4, 5].map((index) => {
            const char = pin[index] || '';
            return (
              <input
                key={index}
                id={index === 0 ? 'whatsappPin' : undefined}
                ref={index === 0 ? pinInputRef : undefined}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={char}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(-1);
                  const newPinArray = pin.split('');
                  while (newPinArray.length < 6) newPinArray.push('');
                  newPinArray[index] = val;

                  if (!e.target.value) {
                    newPinArray[index] = '';
                  }

                  const newPin = newPinArray.join('');
                  setPin(newPin);
                  if (errorMessage) resetError();

                  if (val && index < 5) {
                    const nextInput = e.target.nextElementSibling;
                    if (nextInput) nextInput.focus();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && !char && index > 0) {
                    const prevInput = e.target.previousElementSibling;
                    if (prevInput) prevInput.focus();
                  }
                }}
                disabled={isConnecting}
                className={`flex-1 min-w-0 h-12 sm:h-14 rounded-xl text-center text-[18px] font-medium outline-none transition-colors ${
                  char
                    ? 'bg-[#eef5ef] text-[#4CAF50] border border-[#4CAF50]/20'
                    : 'bg-white text-gray-900 border border-gray-200 focus:border-gray-400 focus:shadow-sm'
                }`}
              />
            );
          })}
        </div>

        <p className="text-[13px] text-gray-500 mt-1">
          Only needed for Cloud API numbers that are not already registered.
        </p>
      </div>

      {errorMessage && (
        <div className="rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 flex flex-col gap-2">
          <p className="text-[13px] text-red-600">{errorMessage}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="text-[13px] font-medium text-gray-900 underline underline-offset-2 w-fit"
          >
            Dismiss and retry
          </button>
        </div>
      )}

      <div className="mt-2">
        <button
          type="button"
          disabled={isConnecting || (!isReady && !errorMessage)}
          onClick={handleConnect}
          className="bg-[#4CAF50] hover:bg-[#43A047] disabled:opacity-50 disabled:cursor-not-allowed text-white text-[14px] font-medium py-2.5 px-5 rounded-lg flex items-center justify-center gap-2 transition-colors w-fit"
        >
          {isConnecting ? (
            <>
              <Spinner className="w-4 h-4 text-white" />
              Connecting…
            </>
          ) : (
            <>
              Connect WhatsApp with Meta
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
